import { faArrowRotateLeft, faChevronUp, faSliders } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Flex,
  Option,
  Select,
  Text,
  TextButton,
  TextWithIcon,
  Tooltip,
} from '@modusign/design-system';
import { endOfDay, startOfDay } from 'date-fns';
import isEqual from 'lodash/isEqual';
import { ReactNode, useState } from 'react';

import RangeDatePicker from '@app/components/DatePicker/RangeDatePicker';
import ExternalDocumentQuantity from '@app/features/externalDocument/components/ExternalDocumentQuantity';
import { EXTERNAL_DOCUMENT_DEFAULT_FROM_YEAR } from '@app/features/externalDocument/constants';
import { LabelSelectButton, Unlabeled, UNLABELED_ID } from '@app/features/label';
import { useMainPageHeaderState } from '@app/hooks/useMainPageHeaderState';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import useUsageCount from '@app/hooks/useUsageCount';

import ExternalDocumentSearchInput from '../../ExternalDocumentSearchInput';

export interface SearchParams {
  query?: string;
  filter: {
    label?: { id: string; name: string } | { id: Unlabeled; name: '라벨 없음' };
    datesOfCompletion: { from: string | null; to: string | null };
    status?: 'EFFECTIVE' | 'EXPIRED' | 'ABORTED';
    documentPeriod: { from: string | null; to: string | null };
  };
}

const initialSearchParams: SearchParams = {
  query: '',
  filter: {
    label: undefined,
    datesOfCompletion: { from: null, to: null },
    status: undefined,
    documentPeriod: { from: null, to: null },
  },
};

export function isInitialSearchParams(value: SearchParams): boolean {
  return isEqual(value, initialSearchParams);
}

export interface SearchFilterProps {
  value: SearchParams;
  onChange: (params: SearchParams) => void;
}

export default function SearchFilter(props: SearchFilterProps) {
  const Component = useResponsiveValue([
    MobileSearchFilter,
    MobileSearchFilter,
    DesktopSearchFilter,
  ]);

  return <Component {...props} />;
}

function DesktopSearchFilter({ value, onChange }: SearchFilterProps) {
  const [{ externalDocuments: documentRegisteredFilter }] = useMainPageHeaderState();
  const [expanded, setExpanded] = useState(!isInitialSearchParams(value));

  const { externalDocumentCount } = useUsageCount();

  if (!expanded) {
    return (
      <Flex alignItems="center" justifyContent="space-between">
        <Flex alignItems="center">
          <ExternalDocumentSearchInput
            documentRegisteredFilter={!!documentRegisteredFilter}
            filter={value.filter}
            sx={{ width: '300px' }}
            onSubmit={(v) => {
              onChange({ ...value, query: v });
              setExpanded(true);
            }}
          />
          <Tooltip content="상세 검색 조건">
            <div>
              <TextWithIcon
                as="button"
                color="textNeutralLight"
                ml={6}
                rightIcon={<FontAwesomeIcon icon={faSliders} />}
                sx={{
                  '&:hover': {
                    textDecoration: 'underline',
                    textUnderlineOffset: '0.2em',
                  },
                  wordBreak: 'keep-all',
                }}
                onClick={() => setExpanded(true)}
              >
                필터
              </TextWithIcon>
            </div>
          </Tooltip>
        </Flex>

        {externalDocumentCount.limit !== Infinity && <ExternalDocumentQuantity />}
      </Flex>
    );
  }

  const appliedFilterCount =
    Object.entries(value.filter).filter(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ([key, value]) => !isEqual(initialSearchParams.filter[key], value)
    ).length + Number(!!value.query);

  return (
    <>
      <Flex alignItems="center" justifyContent="space-between" mb={3}>
        <TextWithIcon
          as="button"
          color="textNeutralLight"
          ml={6}
          rightIcon={
            <Flex
              alignItems="center"
              bg={
                appliedFilterCount ? 'statusBackgroundEmphasisDark' : 'statusBackgroundNeutralBase'
              }
              borderRadius="50%"
              color={appliedFilterCount ? 'white' : 'textNeutralLight'}
              fontSize="xs"
              fontWeight="medium"
              height="18px"
              justifyContent="center"
              width="18px"
            >
              {appliedFilterCount}
            </Flex>
          }
          sx={{
            '&:hover': {
              textDecoration: 'underline',
              textUnderlineOffset: '0.2em',
            },
          }}
          onClick={() => setExpanded(true)}
        >
          필터
        </TextWithIcon>
        {externalDocumentCount.limit !== Infinity && <ExternalDocumentQuantity />}
      </Flex>

      <Flex
        bg="boxBackgroundNeutralSlightlyLight"
        borderRadius="4px"
        flexDirection={['column', null, 'row']}
        flexWrap="wrap"
        pb={6}
        pt={4}
        px={5}
        sx={{ gap: 4 }}
      >
        <Flex sx={{ gap: 4 }}>
          <SearchFilterItem name="검색어">
            <ExternalDocumentSearchInput
              documentRegisteredFilter={!!documentRegisteredFilter}
              filter={value.filter}
              initialInput={value.query}
              sx={{ width: '240px' }}
              onSubmit={(v) => {
                onChange({ ...value, query: v });
              }}
            />
          </SearchFilterItem>
          <SearchFilterItem name="라벨">
            <LabelSelectButton
              label={value.filter.label}
              onChange={(label) => {
                onChange({ ...value, filter: { ...value.filter, label } });
              }}
              onClear={() => {
                onChange({ ...value, filter: { ...value.filter, label: undefined } });
              }}
              onNonLabeledSelect={() => {
                onChange({
                  ...value,
                  filter: { ...value.filter, label: { id: UNLABELED_ID, name: '라벨 없음' } },
                });
              }}
            />
          </SearchFilterItem>
          <SearchFilterItem name="계약 상태">
            <Select
              bg="white"
              value={value.filter.status || 'ALL'}
              width="120px"
              onChange={(e) => {
                if (e.target.value === 'ALL') {
                  onChange({ ...value, filter: { ...value.filter, status: undefined } });
                  return;
                }

                onChange({
                  ...value,
                  filter: {
                    ...value.filter,
                    status: e.target.value as 'EFFECTIVE' | 'EXPIRED' | 'ABORTED',
                  },
                });
              }}
            >
              <Option value="ALL">모든 상태</Option>
              <Option value="EFFECTIVE">계약 이행 중</Option>
              <Option value="EXPIRED">계약 종료</Option>
              <Option value="ABORTED">계약 중단</Option>
            </Select>
          </SearchFilterItem>
        </Flex>
        <Flex alignItems="center" sx={{ gap: 4 }}>
          <SearchFilterItem name="계약 체결일">
            <RangeDatePicker
              captionLayout="dropdown-buttons"
              defaultMonth={
                value.filter.datesOfCompletion.from
                  ? new Date(value.filter.datesOfCompletion.from)
                  : undefined
              }
              field={{
                style: {
                  width: '248px',
                },
                placeholder: 'YY.MM.DD ~ YY.MM.DD',
                onClearDate: () => {
                  onChange({
                    ...value,
                    filter: {
                      ...value.filter,
                      datesOfCompletion: {
                        from: null,
                        to: null,
                      },
                    },
                  });
                },
              }}
              fromYear={EXTERNAL_DOCUMENT_DEFAULT_FROM_YEAR}
              toYear={new Date().getFullYear()}
              value={{
                from: value.filter.datesOfCompletion.from
                  ? new Date(value.filter.datesOfCompletion.from)
                  : undefined,
                to: value.filter.datesOfCompletion.to
                  ? new Date(value.filter.datesOfCompletion.to)
                  : undefined,
              }}
              onSelect={(selectedRange) => {
                const { from, to } = selectedRange || {};
                onChange({
                  ...value,
                  filter: {
                    ...value.filter,
                    datesOfCompletion: {
                      from: from ? startOfDay(from).toISOString() : null,
                      to: to ? endOfDay(to).toISOString() : null,
                    },
                  },
                });
              }}
            />
          </SearchFilterItem>
          <SearchFilterItem name="계약 기간">
            <RangeDatePicker
              captionLayout="dropdown-buttons"
              defaultMonth={
                value.filter.documentPeriod.from
                  ? new Date(value.filter.documentPeriod.from)
                  : undefined
              }
              field={{
                style: { width: '248px' },
                placeholder: 'YY.MM.DD ~ YY.MM.DD',
                onClearDate: () => {
                  onChange({
                    ...value,
                    filter: {
                      ...value.filter,
                      documentPeriod: {
                        from: null,
                        to: null,
                      },
                    },
                  });
                },
              }}
              fromYear={2014}
              toYear={new Date().getFullYear()}
              value={{
                from: value.filter.documentPeriod.from
                  ? new Date(value.filter.documentPeriod.from)
                  : undefined,
                to: value.filter.documentPeriod.to
                  ? new Date(value.filter.documentPeriod.to)
                  : undefined,
              }}
              onSelect={(selectedRange) => {
                const { from, to } = selectedRange || {};

                onChange({
                  ...value,
                  filter: {
                    ...value.filter,
                    documentPeriod: {
                      from: from ? startOfDay(from).toISOString() : null,
                      to: to ? endOfDay(to).toISOString() : null,
                    },
                  },
                });
              }}
            />
          </SearchFilterItem>
          {!isInitialSearchParams(value) && (
            <SearchFilterItem name=" ">
              <TextButton type="reset" onClick={() => onChange(initialSearchParams)}>
                <FontAwesomeIcon icon={faArrowRotateLeft} style={{ fontSize: '12px' }} />
                초기화
              </TextButton>
            </SearchFilterItem>
          )}
        </Flex>
      </Flex>
    </>
  );
}

function MobileSearchFilter({ value, onChange }: SearchFilterProps) {
  const [{ externalDocuments: documentRegisteredFilter }] = useMainPageHeaderState();
  const [expanded, setExpanded] = useState(!isInitialSearchParams(value));

  const { externalDocumentCount } = useUsageCount();

  const appliedFilterCount =
    Object.entries(value.filter).filter(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ([key, value]) => !isEqual(initialSearchParams.filter[key], value)
    ).length + Number(!!value.query);

  if (!expanded) {
    return (
      <>
        {externalDocumentCount.limit !== Infinity && <ExternalDocumentQuantity />}
        <Flex
          alignItems="center"
          bg="boxBackgroundNeutralSlightlyLight"
          borderBottom="1px solid"
          borderBottomColor="boxBorderNeutralLight"
          px={4}
          py={6}
        >
          <Box flex="auto">
            <ExternalDocumentSearchInput
              documentRegisteredFilter={!!documentRegisteredFilter}
              filter={value.filter}
              initialInput={value.query}
              onSubmit={(v) => {
                onChange({ ...value, query: v });
              }}
            />
          </Box>
          <TextWithIcon
            as="button"
            color="textNeutralLight"
            ml={6}
            rightIcon={
              appliedFilterCount === 0 ? (
                <FontAwesomeIcon icon={faSliders} />
              ) : (
                <Flex
                  alignItems="center"
                  bg="statusBackgroundEmphasisDark"
                  borderRadius="50%"
                  color="white"
                  fontSize="xs"
                  fontWeight="medium"
                  height="18px"
                  justifyContent="center"
                  width="18px"
                >
                  {appliedFilterCount}
                </Flex>
              )
            }
            sx={{
              '&:hover': {
                textDecoration: 'underline',
                textUnderlineOffset: '0.2em',
              },
            }}
            onClick={() => setExpanded(true)}
          >
            필터
          </TextWithIcon>
        </Flex>
      </>
    );
  }

  return (
    <>
      {externalDocumentCount.limit !== Infinity && <ExternalDocumentQuantity />}
      <Flex
        alignItems="stretch"
        bg="boxBackgroundNeutralSlightlyLight"
        borderBottom="1px solid"
        borderBottomColor="boxBorderNeutralLight"
        flexDirection="column"
        px={4}
        py={6}
        sx={{ gap: 3 }}
      >
        <SearchFilterItem name="검색어">
          <ExternalDocumentSearchInput
            documentRegisteredFilter={!!documentRegisteredFilter}
            filter={value.filter}
            initialInput={value.query}
            onSubmit={(v) => {
              onChange({ ...value, query: v });
            }}
          />
        </SearchFilterItem>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 3 }}>
          <SearchFilterItem name="라벨">
            <LabelSelectButton
              label={value.filter.label}
              width="100%"
              onChange={(label) => {
                onChange({ ...value, filter: { ...value.filter, label } });
              }}
              onClear={() => {
                onChange({ ...value, filter: { ...value.filter, label: undefined } });
              }}
              onNonLabeledSelect={() => {
                onChange({ ...value, filter: { ...value.filter, label: undefined } });
              }}
            />
          </SearchFilterItem>
          <SearchFilterItem name="계약 상태">
            <Select
              bg="white"
              value={value.filter.status}
              onChange={(e) => {
                if (e.target.value === 'ALL') {
                  onChange({ ...value, filter: { ...value.filter, status: undefined } });
                  return;
                }

                onChange({
                  ...value,
                  filter: {
                    ...value.filter,
                    status: e.target.value as 'EFFECTIVE' | 'EXPIRED' | 'ABORTED',
                  },
                });
              }}
            >
              <Option value="ALL">모든 상태</Option>
              <Option value="EFFECTIVE">계약 이행 중</Option>
              <Option value="EXPIRED">계약 종료</Option>
              <Option value="ABORTED">계약 중단</Option>
            </Select>
          </SearchFilterItem>
        </Box>
        <SearchFilterItem name="계약 체결일">
          <RangeDatePicker
            captionLayout="dropdown-buttons"
            defaultMonth={
              value.filter.datesOfCompletion.from
                ? new Date(value.filter.datesOfCompletion.from)
                : undefined
            }
            field={{
              placeholder: 'YY.MM.DD ~ YY.MM.DD',
              onClearDate: () => {
                onChange({
                  ...value,
                  filter: {
                    ...value.filter,
                    datesOfCompletion: {
                      from: null,
                      to: null,
                    },
                  },
                });
              },
            }}
            fromYear={2014}
            toYear={new Date().getFullYear()}
            value={{
              from: value.filter.datesOfCompletion.from
                ? new Date(value.filter.datesOfCompletion.from)
                : undefined,
              to: value.filter.datesOfCompletion.to
                ? new Date(value.filter.datesOfCompletion.to)
                : undefined,
            }}
            onSelect={(selectedRange) => {
              const { from, to } = selectedRange || {};
              onChange({
                ...value,
                filter: {
                  ...value.filter,
                  datesOfCompletion: {
                    from: from ? startOfDay(from).toISOString() : null,
                    to: to ? endOfDay(to).toISOString() : null,
                  },
                },
              });
            }}
          />
        </SearchFilterItem>
        <SearchFilterItem name="계약 기간">
          <RangeDatePicker
            captionLayout="dropdown-buttons"
            defaultMonth={
              value.filter.documentPeriod.from
                ? new Date(value.filter.documentPeriod.from)
                : undefined
            }
            field={{
              placeholder: 'YY.MM.DD ~ YY.MM.DD',
              onClearDate: () => {
                onChange({
                  ...value,
                  filter: {
                    ...value.filter,
                    documentPeriod: {
                      from: null,
                      to: null,
                    },
                  },
                });
              },
            }}
            fromYear={2014}
            toYear={new Date().getFullYear()}
            value={{
              from: value.filter.documentPeriod.from
                ? new Date(value.filter.documentPeriod.from)
                : undefined,
              to: value.filter.documentPeriod.to
                ? new Date(value.filter.documentPeriod.to)
                : undefined,
            }}
            onSelect={(selectedRange) => {
              const { from, to } = selectedRange || {};

              onChange({
                ...value,
                filter: {
                  ...value.filter,
                  documentPeriod: {
                    from: from ? startOfDay(from).toISOString() : null,
                    to: to ? endOfDay(to).toISOString() : null,
                  },
                },
              });
            }}
          />
        </SearchFilterItem>
        <Flex flexDirection="row-reverse" justifyContent="space-between" mt="10px">
          <TextWithIcon
            as="button"
            color="textNeutralLight"
            iconContainerWidth="11px"
            rightIcon={<FontAwesomeIcon icon={faChevronUp} />}
            type="button"
            onClick={() => setExpanded(false)}
          >
            필터 접기
          </TextWithIcon>
          {!isInitialSearchParams(value) && (
            <TextButton type="reset" onClick={() => onChange(initialSearchParams)}>
              <FontAwesomeIcon icon={faArrowRotateLeft} style={{ fontSize: '12px' }} />
              초기화
            </TextButton>
          )}
        </Flex>
      </Flex>
    </>
  );
}

interface SearchFilterItemProps {
  name: string;
  children: ReactNode;
}

function SearchFilterItem({ name, children }: SearchFilterItemProps) {
  return (
    <Flex flex="none" flexDirection="column">
      <Text
        color="textNeutralVeryLight"
        fontSize="xs"
        fontWeight="medium"
        mb="3px"
        ml={1}
        sx={{ width: '64px' }}
        whiteSpace="pre"
      >
        {name}
      </Text>
      <Box flex="auto">{children}</Box>
    </Flex>
  );
}
